<script setup lang="ts">
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions
} from "@headlessui/vue"
import { ChevronDownIcon } from "@heroicons/vue/outline"
import type { SelectItem } from "@/types/select"

interface Props {
  title?: string
  id: string
  name: string
  hasLabel: boolean
  items: SelectItem[]
  modelValue?: string | number
  placeholder?: string
  disabled?: boolean
}

defineProps<Props>()

const emit = defineEmits(["update:model-value"])

const updateValue = (event: Event) => {
  emit("update:model-value", event)
}
</script>

<template>
  <Listbox
    v-slot="{ open }"
    :modelValue="modelValue"
    as="div"
    @update:model-value="updateValue"
  >
    <ListboxLabel
      v-if="hasLabel"
      class="block text-sm font-medium text-gray-700"
    >
      {{ title }}
    </ListboxLabel>
    <div :class="['relative', hasLabel && 'mt-1']">
      <ListboxButton
        :id="id"
        class="relative w-full h-[38px] py-2 pl-3 pr-10 text-left bg-gray-200 rounded-md shadow-sm cursor-pointer dark:text-gray-200 dark:bg-gray-700 sm:text-sm disabled:cursor-not-allowed disabled:opacity-50"
        data-test-id="open-select-options"
        :disabled="disabled"
      >
        <span :class="['block truncate', !modelValue && 'text-gray-500']">
          {{
            items.find(e => e.value == modelValue)
              ? items.find(e => e.value == modelValue)?.name
              : placeholder
          }}
        </span>
        <span
          class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
        >
          <ChevronDownIcon
            :class="['w-6 h-6 text-placeholderText', open && 'rotate-180']"
            aria-hidden="true"
          />
        </span>
      </ListboxButton>

      <transition
        leave-active-class="transition duration-100 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="absolute z-10 w-full py-1 mt-1 overflow-auto text-base text-gray-200 bg-gray-800 border border-gray-700 rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
        >
          <ListboxOption
            v-for="(item, index) in items"
            :key="item.value"
            v-slot="{ active, selected }"
            as="template"
            :value="item.value"
            @change="updateValue(selected)"
          >
            <li
              :class="[
                active ? 'bg-gray-700' : 'text-gray-200',
                index + 1 !== items.length ? 'border-b border-gray-700' : null,
                'cursor-pointer select-none relative py-2 pl-3 pr-9 text-left'
              ]"
              :data-test-id="`option-${item.value}`"
            >
              <span
                :class="[
                  selected ? 'font-semibold' : 'font-normal',
                  'block truncate'
                ]"
              >
                {{ item.name }}
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>
  </Listbox>
</template>
